import { useQuery } from '@tanstack/react-query';
import { getSyncStatus, type WhatsAppSyncJob } from '@/services/whatsappSync';

/**
 * Story 26.3 — single source of truth for the Inbox's terminal state.
 *
 * The Inbox is always in exactly one of three mutually-exclusive states. The
 * server resolves the discriminant (`phase`) in getLatestSyncStatus; the client
 * never re-derives it from the legacy flags.
 */
export type InboxState =
  | { phase: 'DISCONNECTED'; lastConnectedAt?: string }
  | { phase: 'FAILED'; jobId?: string; errorMessage?: string | null; isFirstSync: boolean }
  | {
      phase: 'SYNCING';
      percent: number;
      processed: number;
      total: number;
      // Story 26.12 — message-import progress; >0 means we're in the message phase
      // (drives the gate's "Baixando mensagens…" copy so it never looks frozen).
      messagesProcessed: number;
      isCancellable: boolean;
      isFirstSync: boolean;
      // True only while the FIRST sync-status response is in flight (no server phase yet).
      // The Inbox renders a neutral spinner for this — NOT the "Baixando conversas" gate —
      // so a fast resolve to READY doesn't flash the download gate over an empty inbox.
      isInitialLoading?: boolean;
    }
  // Story 26.6 (AC2) — READY may carry a non-blocking complementary (delta) sync flag.
  // 41.9 (RC-014): READY carrega o desfecho do último sync — parcial nunca vira verde pleno.
  | { phase: 'READY'; isComplementarySyncing?: boolean; lastSyncOutcome?: 'completed' | 'completed_with_errors' | null; lastSyncWarning?: string | null };

export function useInboxState(): InboxState {
  const { data, isLoading } = useQuery<WhatsAppSyncJob | null>({
    queryKey: ['inbox', 'state'],
    queryFn: getSyncStatus,
    // Poll while gating; also keep polling while a complementary sync runs so the
    // lightweight indicator clears when it finishes. Stop once READY and idle.
    refetchInterval: (query) => {
      const d = query.state.data;
      return d?.phase === 'READY' && !d?.isComplementarySyncing ? false : 2000;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  // Before the first response, render a neutral loading spinner (isInitialLoading) rather
  // than flashing DISCONNECTED or the "Baixando conversas" gate — so a fast resolve to
  // READY (empty inbox) never shows the download gate for a split second.
  if (isLoading) {
    return { phase: 'SYNCING', percent: 0, processed: 0, total: 0, messagesProcessed: 0, isCancellable: false, isFirstSync: true, isInitialLoading: true };
  }

  if (!data || data.phase === 'DISCONNECTED') {
    return { phase: 'DISCONNECTED', lastConnectedAt: data?.lastConnectedAt ?? undefined };
  }

  if (data.phase === 'READY') {
    return {
      phase: 'READY',
      isComplementarySyncing: data.isComplementarySyncing ?? false,
      lastSyncOutcome: data.lastSyncOutcome ?? null,
      lastSyncWarning: data.lastSyncWarning ?? null,
    };
  }

  if (data.phase === 'FAILED') {
    return {
      phase: 'FAILED',
      jobId: data.id,
      errorMessage: data.errorMessage,
      isFirstSync: data.isFirstSync ?? true,
    };
  }

  // SYNCING (explicit, or any non-terminal legacy fallback).
  return {
    phase: 'SYNCING',
    percent: data.percent ?? 0,
    processed: data.processed ?? 0,
    total: data.totalContacts ?? 0,
    messagesProcessed: data.messagesProcessed ?? 0,
    isCancellable: data.isCancellable ?? false,
    isFirstSync: data.isFirstSync ?? true,
  };
}
